import React from 'react';
import { Order } from '../../lib/types';
import { formatCurrency, formatDate } from '../../lib/utils';
import { StatusPill } from '../ui';
import { ExpandedRowContent } from './ExpandedRowContent';
import type { OrderAuditEntry } from '../../lib/api';

interface OrderRowProps {
  order:      Order;
  index:      number;       // display index (1-based already computed)
  currency:   string;
  locale:     string;
  expanded:   boolean;
  onToggleExpand: (id: string) => void;
  onStatusClick:  (order: Order, e: React.MouseEvent) => void;
  onMenuClick:    (order: Order, e: React.MouseEvent) => void;
  auditOpen:      boolean;
  auditLoading:   boolean;
  auditLog:       OrderAuditEntry[];
  onToggleAudit:  (id: string) => void;
}

function rowClass(o: Order): string {
  if (o.status === 'Pending') return 'rr';
  if (!o.order_number || !o.total_cost) return 'ra';
  return 'rg';
}

export function OrderRow({
  order, index, currency, locale, expanded,
  onToggleExpand, onStatusClick, onMenuClick,
  auditOpen, auditLoading, auditLog, onToggleAudit,
}: OrderRowProps) {
  const items = order.order_items ?? [];
  const primary = items[0];
  const extra = items.length - 1;

  return (
    <>
      <tr className={rowClass(order)}>
        <td><span className="text-sm text-subtle">{index}</span></td>
        <td className="text-sm text-muted">{formatDate(order.order_date, locale)}</td>
        <td
          style={{ maxWidth: 220, cursor: 'pointer' }}
          onClick={() => onToggleExpand(order.id)}
        >
          {items.length === 0 ? (
            <span className="text-xs text-subtle">—</span>
          ) : (
            <>
              <div className="text-base font-medium">{primary?.item_name || '—'}</div>
              <div className="text-sm text-accent" style={{ marginTop: 1 }}>
                {expanded
                  ? `${items.length} item${items.length !== 1 ? 's' : ''} ▴`
                  : extra > 0 ? `+${extra} more ▾` : 'details ▾'}
              </div>
            </>
          )}
        </td>
        <td>{order.vendor || '—'}</td>
        <td className="text-mono text-sm text-muted">{order.order_number || '—'}</td>
        <td className="text-muted">{order.department_name || '—'}</td>
        <td>
          <StatusPill
            status={order.status}
            onClick={e => onStatusClick(order, e)}
            withCaret
          />
        </td>
        <td className="text-right font-semibold">
          {order.total_cost ? formatCurrency(order.total_cost, currency) : '—'}
        </td>
        <td style={{ position: 'relative' }}>
          <button className="row-menu-btn" onClick={e => onMenuClick(order, e)} title="Actions">
            <svg viewBox="0 0 16 16">
              <circle cx="8" cy="3.5" r="1.1" />
              <circle cx="8" cy="8"   r="1.1" />
              <circle cx="8" cy="12.5" r="1.1" />
            </svg>
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} style={{ padding: '0 12px 10px 12px' }}>
            <ExpandedRowContent
              order={order}
              currency={currency}
              locale={locale}
              auditOpen={auditOpen}
              auditLoading={auditLoading}
              auditLog={auditLog}
              onToggleAudit={() => onToggleAudit(order.id)}
            />
          </td>
        </tr>
      )}
    </>
  );
}
